import React, { useEffect, useState, useRef } from 'react';
import './ScreenMapView.css';
import { useNavigate } from 'react-router-dom';
import { RingLoader } from 'react-spinners';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { SCREEN_DISPLAY_LIMITS, truncateDisplay } from '../../../../utils/screenDisplayUtils';
import { isScreenActive } from '../../../../utils/screenActiveStatus';
import { buildScreenMapMarkerIcon } from '../../../../utils/screenMapMarkerIcon';
import { useFleetScreensQuery } from '../../../../query/dashboardQueries';

/**
 * Screen Map View — fleet map/grid with hardware metadata (brand, type, dimensions, etc.).
 * Data from shared React Query cache (`/getScreenRealTimeStatus`).
 */
export default function ScreenMapView() {
    const navigate = useNavigate();
    const { data, isLoading, isFetching, error: queryError, refetch } = useFleetScreensQuery();
    const screens = data?.screens || [];
    const [selectedScreen, setSelectedScreen] = useState(null);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('map');
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const markersRef = useRef({});

    const showInitialLoading = isLoading && screens.length === 0;

    useEffect(() => {
        if (queryError) {
            setError('Failed to load screen data. Please try again.');
        } else {
            setError(null);
        }
    }, [queryError]);

    function handleScreenClick(screen) {
        setSelectedScreen(selectedScreen?.screenId === screen.screenId ? null : screen);
    }

    function focusMapOnScreen(screen) {
        if (!mapInstanceRef.current || !screen?.latitude || !screen?.longitude) return;

        const marker = markersRef.current[screen.screenId];
        mapInstanceRef.current.flyTo([screen.latitude, screen.longitude], 15, {
            animate: true,
            duration: 0.6,
        });

        if (marker) {
            marker.openPopup();
        }
    }

    function handleSidebarScreenClick(screen) {
        handleScreenClick(screen);
        focusMapOnScreen(screen);
    }

    function escapePopupText(value) {
        if (value === undefined || value === null) {
            return '';
        }
        return String(value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }

    /** Map marker popup: id + live status only (full metadata is on the screen info page). */
    function buildMapPopupHtml(screen, isActive) {
        return (
            `<div class="screen-popup">`
            + `<strong>Screen #${escapePopupText(screen.screenId)}</strong>`
            + `<p style="margin:6px 0 0;">Status: ${isActive ? '🟢 Active' : '⚫ Inactive'}</p>`
            + `</div>`
        );
    }

    function displayMeta(value) {
        if (value === undefined || value === null || String(value).trim() === '') {
            return 'N/A';
        }
        return truncateDisplay(String(value), SCREEN_DISPLAY_LIMITS.gridMetric);
    }

    function initializeMap() {
        if (mapInstanceRef.current) return;
        
        // Center coordinates (can be updated based on screens data)
        const centerLat = screens.length > 0 && screens[0].latitude ? screens[0].latitude : 40.7128;
        const centerLng = screens.length > 0 && screens[0].longitude ? screens[0].longitude : -74.0060;
        
        const map = L.map(mapRef.current).setView([centerLat, centerLng], 12);
        
        // Add OpenStreetMap tiles
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; OpenStreetMap contributors',
            maxZoom: 19,
            referrerPolicy: 'strict-origin-when-cross-origin',
        }).addTo(map);
        
        mapInstanceRef.current = map;
        addScreenMarkers(map);
    }

    function addScreenMarkers(map) {
        // Clear existing markers
        Object.values(markersRef.current).forEach(marker => {
            map.removeLayer(marker);
        });
        markersRef.current = {};
        
        screens.forEach(screen => {
            if (!screen.latitude || !screen.longitude) return;
            
            const isActive = isScreenActive(screen);
            const markerIcon = buildScreenMapMarkerIcon({
                isActive,
                type: screen.type ?? screen.Type,
            });

            const marker = L.marker([screen.latitude, screen.longitude], { icon: markerIcon })
                .bindPopup(buildMapPopupHtml(screen, isActive))
                .on('click', () => {
                    navigate(`/advertiserDashboard/screen/${screen.screenId}`);
                })
                .addTo(map);
            
            markersRef.current[screen.screenId] = marker;
            
            // Highlight selected marker
            if (selectedScreen?.screenId === screen.screenId) {
                marker.openPopup();
            }
        });
    }

    function fitMapBounds() {
        if (!mapInstanceRef.current || screens.length === 0) return;
        
        const validScreens = screens.filter(s => s.latitude && s.longitude);
        if (validScreens.length === 0) return;
        
        const bounds = L.latLngBounds(
            validScreens.map(s => [s.latitude, s.longitude])
        );
        
        mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50] });
    }

    useEffect(() => {
        if (activeTab !== 'map' || showInitialLoading) {
            return undefined;
        }

        const timer = setTimeout(() => {
            if (!mapRef.current) {
                return;
            }
            if (screens.length === 0) {
                if (mapInstanceRef.current) {
                    try {
                        mapInstanceRef.current.remove();
                    } catch (e) {
                        console.warn('Leaflet map remove:', e);
                    }
                    mapInstanceRef.current = null;
                    markersRef.current = {};
                }
                return;
            }
            if (!mapInstanceRef.current) {
                initializeMap();
            } else {
                mapInstanceRef.current.invalidateSize();
                addScreenMarkers(mapInstanceRef.current);
            }
            fitMapBounds();
        }, 120);

        return () => clearTimeout(timer);
    }, [activeTab, screens, showInitialLoading, selectedScreen]);

    const activeScreensCount = screens.filter(s => isScreenActive(s)).length;
    const mappedScreensCount = screens.filter((s) => s.latitude && s.longitude).length;

    return (
        <div className='screenMapViewContainer'>
            <div className='screenMapViewHeader'>
                <div className='screenMapViewTitle'>
                    <h1>Screen Network</h1>
                    <span className='screenMapViewSubtitle'>
                        Interactive view of all available screens in the network
                    </span>
                </div>
                
                <div className='screenMapViewControls'>
                    <div className='screenMapViewStats'>
                        <div className='stat'>
                            <span className='statLabel'>Active Screens</span>
                            <span className='statValue'>{activeScreensCount}/{screens.length}</span>
                        </div>
                        <div className='stat'>
                            <span className='statLabel'>On map</span>
                            <span className='statValue'>{mappedScreensCount}</span>
                        </div>
                    </div>
                    
                    <button 
                        className='screenMapRefreshBtn'
                        onClick={() => refetch()}
                        disabled={isFetching}
                    >
                        {isFetching ? '↻ Refreshing...' : '↻ Refresh'}
                    </button>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className='screenViewTabs'>
                <button 
                    className={`tabButton ${activeTab === 'map' ? 'active' : ''}`}
                    onClick={() => setActiveTab('map')}
                >
                    Map View
                </button>
                <button 
                    className={`tabButton ${activeTab === 'grid' ? 'active' : ''}`}
                    onClick={() => setActiveTab('grid')}
                >
                    Grid View
                </button>
            </div>

            {/* Tab Content - Grid View */}
            {activeTab === 'grid' && (
            <div className='screenGridContainer'>
                {error && (
                    <div className='noScreensMessage' style={{color: '#C52F00', gridColumn: '1 / -1'}}>
                        <span>{error}</span>
                    </div>
                )}
                {showInitialLoading ? (
                    <div className='screenMapLoadingOverlay'>
                        <RingLoader
                            color={'#C52F00'}
                            loading={true}
                            size={50}
                            aria-label="Loading Spinner"
                            data-testid="loader"
                        />
                    </div>
                ) : screens.length > 0 ? (
                    screens.map((screen) => {
                        const isActive = isScreenActive(screen);
                        const addressTitle = truncateDisplay(
                            screen.address || `Screen ${screen.screenId}`,
                            SCREEN_DISPLAY_LIMITS.addressLine
                        );
                        const lastActiveShort = screen.lastActive
                            ? truncateDisplay(new Date(screen.lastActive).toLocaleString(), 36)
                            : 'Never';
                        return (
                        <div
                            key={screen.screenId}
                            className={`screenCard ${isActive ? 'active' : 'inactive'}`}
                        >
                            <div className='screenCardHeader'>
                                <div className='screenCardStatus'>
                                    <span className={`statusIndicator ${isActive ? 'active' : 'inactive'}`}></span>
                                    <span className='statusText'>
                                        {isActive ? 'Active' : 'Inactive'}
                                    </span>
                                </div>
                                <span className='screenId'>#{screen.screenId}</span>
                            </div>

                            <div className='screenCardContent'>
                                <h3 className='screenLocation'>{addressTitle}</h3>
                            </div>

                            <div className='screenCardMetrics'>
                                <div className='metric'>
                                    <span className='metricLabel'>Brand</span>
                                    <span className='metricValue'>{displayMeta(screen.brand ?? screen.Brand)}</span>
                                </div>
                                <div className='metric'>
                                    <span className='metricLabel'>Type</span>
                                    <span className='metricValue'>{displayMeta(screen.type ?? screen.Type)}</span>
                                </div>
                                <div className='metric'>
                                    <span className='metricLabel'>Last Active</span>
                                    <span className='metricValue' title={screen.lastActive ? new Date(screen.lastActive).toLocaleString() : ''}>
                                        {lastActiveShort}
                                    </span>
                                </div>
                            </div>

                            <div className='screenCardFooter'>
                                <button
                                    type='button'
                                    className='screenCardMoreInfoBtn'
                                    onClick={() => navigate(`/advertiserDashboard/screen/${screen.screenId}`)}
                                >
                                    More info
                                </button>
                            </div>
                        </div>
                        );
                    })
                ) : (
                    <div className='noScreensMessage'>
                        <span>No screens available</span>
                    </div>
                )}
            </div>
            )}

            {/* Map stays mounted when switching tabs so Leaflet keeps a valid container; visibility toggled via class. */}
            <div
                className={`screenMapContainer ${activeTab === 'map' ? 'screenMapContainer--active' : 'screenMapContainer--hidden'}`}
                aria-hidden={activeTab !== 'map'}
            >
                {error && (
                    <div className='noScreensMessage' style={{ color: '#C52F00' }}>
                        <span>{error}</span>
                    </div>
                )}
                <div className='mapLayout'>
                    {showInitialLoading && activeTab === 'map' && (
                        <div className='screenMapLoadingOverlay'>
                            <RingLoader
                                color={'#C52F00'}
                                loading={true}
                                size={50}
                                aria-label="Loading Spinner"
                                data-testid="loader"
                            />
                        </div>
                    )}
                    <div className='mapContainer' ref={mapRef} />
                    {screens.length > 0 && (
                        <div className='screensList'>
                            <div className='screensListHeader'>
                                <h4>Screen Locations</h4>
                                <p>{screens.filter((s) => isScreenActive(s)).length} active of {screens.length}</p>
                            </div>
                            <div className='screensListContent'>
                                {screens.map((screen) => {
                                    const isActive = isScreenActive(screen);
                                    return (
                                        <div
                                            key={screen.screenId}
                                            className={`screenListItem ${isActive ? 'active' : 'inactive'} ${selectedScreen?.screenId === screen.screenId ? 'selected' : ''}`}
                                            onClick={() => handleSidebarScreenClick(screen)}
                                        >
                                            <div className='listItemStatus'>
                                                <span className={`statusDot ${isActive ? 'active' : 'inactive'}`}></span>
                                            </div>
                                            <div className='listItemContent'>
                                                <div className='listItemId'>#{screen.screenId}</div>
                                                <div className='listItemAddress' title={screen.address || ''}>
                                                    {truncateDisplay(screen.address || 'Unknown', SCREEN_DISPLAY_LIMITS.addressLine)}
                                                </div>
                                                {screen.latitude && screen.longitude && (
                                                    <div className='listItemCoords'>
                                                        {screen.latitude.toFixed(2)}, {screen.longitude.toFixed(2)}
                                                    </div>
                                                )}
                                                {(screen.brand || screen.Brand || screen.type || screen.Type) && (
                                                    <div className='listItemMeta' title={[screen.brand || screen.Brand, screen.type || screen.Type].filter(Boolean).join(' · ')}>
                                                        {truncateDisplay(
                                                            [screen.brand || screen.Brand, screen.type || screen.Type].filter(Boolean).join(' · '),
                                                            SCREEN_DISPLAY_LIMITS.brandTypeLine
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            <button
                                                type='button'
                                                className='screenListItemMoreBtn'
                                                aria-label='Screen details'
                                                title='Screen details'
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(`/advertiserDashboard/screen/${screen.screenId}`);
                                                }}
                                            >
                                                ⋯
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
                {screens.length === 0 && !showInitialLoading && (
                    <div className='noScreensMessage'>
                        <span>No screens available</span>
                    </div>
                )}
            </div>
        </div>
    );
}
